import {
    Component,
    Input,
    Output,
    OnInit,
    EventEmitter,
    ViewChild
} from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import * as uuid from 'uuid';

import { NeptuneApi } from '../../../../../api/genericApi/neptune.api';
import { HttpVerbsEnum } from '../../../../../api/shared/enums/http-verbs.enum';
import { AbstractAiDialogComponent } from '../../../../../Common/Dialogs/components/AbstractAIDialog/Abstract-AI-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { CompletionToneEnum } from '@api/ai/enums/completion-tone.enum';
import { promptLanguageOptions } from '@Common/Forms/data/prompt-language-options';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { ContactModel } from '@Common/TargetingShared/model/contact.model';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { RichTextEditorConfigModel } from '@Common/Forms/components/RichTextEditorCK/model/rich-text-editor-config.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';


enum DesiredOutputType {
    NONE = 'NONE',
    EARN_MEDIA = 'EARN_MEDIA',
    ENGAGE = 'ENGAGE',
    COLLABORATE = 'COLLABORATE',
    BUILD_RELATIONSHIP = 'BUILD_RELATIONSHIP',
    CUSTOM = 'CUSTOM'
}


enum DesiredOutputSubType {
    /** Earn Media sub-type options */
    PRESS_RELEASE = 'PRESS_RELEASE',
    FEATURE_STORY = 'FEATURE_STORY',
    ARTICLE = 'ARTICLE',
    PROFILE_PIECE = 'PROFILE_PIECE',
    OP_ED = 'OP_ED',
    GUEST_POST = 'GUEST_POST',
    CASE_STUDY = 'CASE_STUDY',
    SOCIAL_MEDIA = 'SOCIAL_MEDIA',
    PRODUCT_REVIEW = 'PRODUCT_REVIEW',
    PRODUCT_ENDORSEMENT = 'PRODUCT_ENDORSEMENT',
    SYNDICATED_CONTENT = 'SYNDICATED_CONTENT',
    NEWSLETTER = 'NEWSLETTER',
    VIDEO = 'VIDEO',
    PHOTO_SHOOT = 'PHOTO_SHOOT',
    ROUNDUP = 'ROUNDUP',
    PODCAST = 'PODCAST',
    /** Engage sub-type options */
    MEETING = 'MEETING',
    INTERVIEW = 'INTERVIEW',
    EVENT_ATTENDANCE = 'EVENT_ATTENDANCE',
    PERSONAL_CONNECTION = 'PERSONAL_CONNECTION',
    NETWORK_CONNECTION = 'NETWORK_CONNECTION',
    PRODUCT_SAMPLE = 'PRODUCT_SAMPLE',
    /** Collaborate sub-type options */
    PROJECT = 'PROJECT',
    WEBINAR_WORKSHOP = 'WEBINAR_WORKSHOP',
    SURVEY_POLL = 'SURVEY_POLL',
    APPLICATION_COMPLETION = 'APPLICATION_COMPLETION',
    SEGMENT_SPONSORSHIP = 'SEGMENT_SPONSORSHIP',
    ARTICLE_SPONSORSHIP = 'ARTICLE_SPONSORSHIP',
    /** Build a Relationship sub-type options */
    RAPPORT = 'RAPPORT'
}

enum UrgencyType {
    NONE = 'NONE',
    TIME_SENSITIVE = 'TIME_SENSITIVE',
    EXTREMELY_TIME_SENSITIVE = 'EXTREMELY_TIME_SENSITIVE'
}

enum LengthType {
    SHORT = 'SHORT',
    MEDIUM = 'MEDIUM',
    LONG = 'LONG'
}

interface PersonalizationWorkBenchApiModel {
    desiredOutcome: string;
    customDesiredOutcome: string | null;
    urgency: string;
    tone: string;
    length: string;
    useRecipientLanguage: boolean;
    personalNote: string;
}

interface PersonalizationWorkbenchDefault {
    desiredOutcome: DesiredOutputType;
    desiredOutcomeSub: DesiredOutputSubType | null;
    customDesiredOutcome: string | null;
    tone: CompletionToneEnum;
    length: LengthType;
    urgency: UrgencyType;
    language: string;
    useRecipientLanguage: boolean;
}

interface DisclaimerSession {
    hideDisclaimer: boolean;
}


@Component({
    selector: 'agpr-personalization-workbench-dialog',
    templateUrl: './personalization-workbench-dialog.component.html',
    styleUrls: ['./personalization-workbench-dialog.component.scss'],
    standalone: false
})
export class PersonalizationWorkbenchDialogComponent extends AbstractAiDialogComponent implements OnInit {

    @Input() personalizationType: PersonalizationTagEnum.INTRO | PersonalizationTagEnum.OUTRO;
    @Input() set userText(text: string) {
        this.generatedContent = text;
        this.generatedContentOriginal = text;
    }
    @Input() recipient: ContactModel | OutletModel;
    @Input() distributionId: number;
    @Input() aiEnabled: boolean = false;

    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() overlayContainer: Element;

    @Output() contentChanges = new EventEmitter<string>();

    @ViewChild('rightScrollPanel') rightScrollPanel: ScrollPanelComponent;

    generatedContentOriginal: string = '';

    hideSlideOverlay: boolean = false;

    loading: boolean = false;
    hasGenerated: boolean = false;
    hideDisclaimer: boolean = false;
    storedHideDisclaimer: boolean = false;
    disclaimerState: boolean = false;
    streamingStarted: boolean = false;

    ckEditorConfig: RichTextEditorConfigModel;

    customDesiredOutcome: string = '';
    personalNotes: string = '';
    useRecipientLanguage: boolean = false;

    isTextExceedingLimit: boolean = false;

    readonly desiredOutcomeOptions: DropdownOptionModel<DesiredOutputType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.noSelection',       DesiredOutputType.NONE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.earnMedia',         DesiredOutputType.EARN_MEDIA),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.engage',            DesiredOutputType.ENGAGE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.collaborate',       DesiredOutputType.COLLABORATE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.buildRelationship', DesiredOutputType.BUILD_RELATIONSHIP),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.custom',            DesiredOutputType.CUSTOM),
    ];
    readonly earnMediaSubOptions: DropdownOptionModel<DesiredOutputSubType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.pressRelease',       DesiredOutputSubType.PRESS_RELEASE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.featureStory',       DesiredOutputSubType.FEATURE_STORY),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.article',            DesiredOutputSubType.ARTICLE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.profilePiece',       DesiredOutputSubType.PROFILE_PIECE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.opEd',               DesiredOutputSubType.OP_ED),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.guestPost',          DesiredOutputSubType.GUEST_POST),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.caseStudy',          DesiredOutputSubType.CASE_STUDY),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.socialMedia',        DesiredOutputSubType.SOCIAL_MEDIA),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.productReview',      DesiredOutputSubType.PRODUCT_REVIEW),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.productEndorsement', DesiredOutputSubType.PRODUCT_ENDORSEMENT),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.syndicated',         DesiredOutputSubType.SYNDICATED_CONTENT),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.newsletter',         DesiredOutputSubType.NEWSLETTER),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.video',              DesiredOutputSubType.VIDEO),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.photoShoot',         DesiredOutputSubType.PHOTO_SHOOT),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.roundup',            DesiredOutputSubType.ROUNDUP),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.podcast',            DesiredOutputSubType.PODCAST),
    ];
    readonly engageSubOptions: DropdownOptionModel<DesiredOutputSubType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.meeting',           DesiredOutputSubType.MEETING),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.interview',         DesiredOutputSubType.INTERVIEW),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.attendEvent',       DesiredOutputSubType.EVENT_ATTENDANCE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.connectPersonally', DesiredOutputSubType.PERSONAL_CONNECTION),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.network',           DesiredOutputSubType.NETWORK_CONNECTION),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.productSample',     DesiredOutputSubType.PRODUCT_SAMPLE),
    ];
    readonly collaborateSubOptions: DropdownOptionModel<DesiredOutputSubType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.collaborateProject',  DesiredOutputSubType.PROJECT),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.convinceWorkshop',    DesiredOutputSubType.WEBINAR_WORKSHOP),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.convincePoll',        DesiredOutputSubType.SURVEY_POLL),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.convinceApplication', DesiredOutputSubType.APPLICATION_COMPLETION),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.sponsorSegment',      DesiredOutputSubType.SEGMENT_SPONSORSHIP),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.sponsorArticle',      DesiredOutputSubType.ARTICLE_SPONSORSHIP),
    ];
    readonly buildRelationshipSubOptions: DropdownOptionModel<DesiredOutputSubType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.buildRapport', DesiredOutputSubType.RAPPORT)
    ];
    readonly toneOptions: DropdownOptionModel<CompletionToneEnum>[] = [
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.professional', CompletionToneEnum.PROFESSIONAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.formal',       CompletionToneEnum.FORMAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.informal',     CompletionToneEnum.INFORMAL),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.fun',          CompletionToneEnum.FUN),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.friendly',     CompletionToneEnum.FRIENDLY),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.optimistic',   CompletionToneEnum.OPTIMISTIC),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.assertive',    CompletionToneEnum.ASSERTIVE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.cooperative',  CompletionToneEnum.COOPERATIVE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.sincere',      CompletionToneEnum.SINCERE),
        new DropdownOptionModel('Common.dialog.aiHelper.contentTone.humorous',     CompletionToneEnum.HUMOROUS),

    ];
    readonly urgencyOptions: DropdownOptionModel<UrgencyType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.notTimeSensitive',       UrgencyType.NONE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.timeSensitive',          UrgencyType.TIME_SENSITIVE),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.extremelyTimeSensitive', UrgencyType.EXTREMELY_TIME_SENSITIVE),
    ];
    readonly lengthOptions: DropdownOptionModel<LengthType>[] = [
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.short',  LengthType.SHORT),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.medium', LengthType.MEDIUM),
        new DropdownOptionModel('Discover.distribution.personalizationWorkbench.long',   LengthType.LONG),
    ];
    readonly languageOptions: DropdownOptionModel[] = promptLanguageOptions;

    desiredOutcome: DropdownOptionModel<DesiredOutputType> = this.desiredOutcomeOptions[0];
    desiredOutcomeSubOptions: DropdownOptionModel<DesiredOutputSubType>[] = [];
    desiredOutcomeSub: DropdownOptionModel<DesiredOutputSubType>;

    urgency: DropdownOptionModel<UrgencyType> = this.urgencyOptions[0];
    length: DropdownOptionModel<LengthType> = this.lengthOptions[1];
    language: DropdownOptionModel;
    tone: DropdownOptionModel<CompletionToneEnum> = this.toneOptions[0];

    private subscriptions: Subscription[] = [];
    private readonly defaultSelectionSessionKey: string = 'personalization-workbench-defaultSelections';
    private readonly disclaimerSessionKey: string = 'personalization-workbench-disclaimer';


    readonly id = 'personalizationWorkbenchDialog';
    readonly buttonTypes = ButtonTypesEnum;
    readonly desiredOutputType = DesiredOutputType;
    readonly personalizationEnum = PersonalizationTagEnum;
    readonly personalizationTypeLabelMap = new Map<PersonalizationTagEnum, string>([
        [PersonalizationTagEnum.INTRO, 'Discover.distribution.personalizationWorkbench.intro'],
        [PersonalizationTagEnum.OUTRO, 'Discover.distribution.personalizationWorkbench.outro'],
    ]);

    constructor(
        public coreDialogService: CoreDialogService,
        public configService: ConfigService,
        private currentUserStateService: CurrentUserStateService,
        private messageService: MessageService,
        private neptuneApi: NeptuneApi
    ) {
        super(coreDialogService, 'PersonalizationWorkbenchDialog');
    }

    ngOnInit() {
        this.ckEditorConfig = new RichTextEditorConfigModel({
            enableInsertImage: false,
            enableLineHeight: true,
            enableFontColor: false,
            enableBackgroundColor: false,
            enableHtmlEditor: false,
            enableFontFamily: true,
            enableFontSize: true,
            enableBlockQuote: false,
            enableHeading: false,
            enableAlignment: false,
            enableDirection: false,
            enableIndent: false,
            enableAIRefinement: true,
            enableAIHelper: false,
            enableTranslation: false,
            hideInsert: true,
            aiReferenceId: ''
        });

        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().subscribe(langTag => {
                const referenceLang = this.languageOptions.find(x => x.value === langTag);

                if (referenceLang) {
                    this.language = referenceLang;
                } else {
                    this.language = this.languageOptions.find(x => x.value === 'en-US');
                }
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.languageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            })
        );

        this.loadSessionDefaults();
    }

    generate() {
        this.generatedContent = '';
        this.hasGenerated = true;
        this.loading = true;

        const type = this.personalizationType === PersonalizationTagEnum.INTRO ? 'intros' : 'outros';
        let entitySegment: string = '';
        let employmentId: number = null;

        if (this.recipient.entityType === EntityType.CONTACT) {
            entitySegment = `contact/${this.recipient.id}`;
            employmentId = (this.recipient as ContactModel).selectedEmployment ?
                (this.recipient as ContactModel).selectedEmployment.id : null;
        } else {
            entitySegment = `outlet/${this.recipient.id}`;
        }

        let url = `/distributions/${this.distributionId}/personalizations/${entitySegment}/${type}?locale=${this.language.value}`;

        if (employmentId !== undefined && employmentId !== null) {
            url += `&contextualEmploymentId=${employmentId}`;
        }

        this.fetchData(url);
    }

    override fetchData(url: string, aiReferenceId?: string) {

        if (!aiReferenceId) {
            aiReferenceId = uuid.v4();
        }

        this.neptuneApi.api<string>(
            url,
            this.getRequestModel(),
            HttpVerbsEnum.POST,
            null,
            'text'
        ).pipe(
            map(response => {
                // handle OpenAI bug that returns undesired fence code block wrapper
                if (response.startsWith('```json')) {
                    response = response.replace('```json', '');
                }

                if (response.startsWith('json')) {
                    response = response.replace('json', '');
                }

                if (response.startsWith('```html')) {
                    response = response.replace('```html', '');
                }

                if (response.startsWith('html')) {
                    response = response.replace('html', '');
                }

                if (response.endsWith('```')) {
                    response = response.replace('```', '');
                }

                // replace Carriage Return Line Feed with break html tags
                response = response.replace(/(\r\n|\r|\n|\u000D\u000A)/g, '<br>').trimStart();
                return response;
            }),
        ).subscribe({
            next: (response: string) => {
                this.generatedContent = response;
                this.onComplete();
            },
            error: (error) => {
                if (error?.message && error.message.includes('signal is aborted without reason')) {
                    return;
                }
                this.handleError();
            }
        });
    }

    getRequestModel() {
        let desiredOutcome: string = '';

        if (this.desiredOutcome.value === DesiredOutputType.CUSTOM || this.desiredOutcome.value === DesiredOutputType.NONE) {
            desiredOutcome = this.desiredOutcome.value;
        } else {
            // combined DesiredOutputType & DesiredOutputSubType will match b/e enums
            desiredOutcome = this.desiredOutcome.value + '_' + this.desiredOutcomeSub.value;
        }

        const api: PersonalizationWorkBenchApiModel = {
            desiredOutcome: desiredOutcome,
            customDesiredOutcome: (this.desiredOutcome.value === DesiredOutputType.CUSTOM) ? this.customDesiredOutcome : null,
            urgency: this.urgency.value,
            tone: this.tone.value,
            length: this.length.value,
            useRecipientLanguage: this.useRecipientLanguage,
            personalNote: this.personalNotes
        };

        return api;
    }


    use() {
        this.contentChanges.emit(this.generatedContent);
        this.close();
    }


    /** save defaults in session storage and close dialog */
    close() {
        if (this.aiEnabled) {
            const defaults: PersonalizationWorkbenchDefault = {
                desiredOutcome: this.desiredOutcome.value,
                desiredOutcomeSub: (this.desiredOutcome.value === DesiredOutputType.CUSTOM || this.desiredOutcome.value === DesiredOutputType.NONE)
                    ? null : this.desiredOutcomeSub.value,
                customDesiredOutcome: (this.desiredOutcome.value === DesiredOutputType.CUSTOM)
                    ? this.customDesiredOutcome : null,
                tone: this.tone.value,
                length: this.length.value,
                urgency: this.urgency.value,
                language: this.language.value,
                useRecipientLanguage: this.useRecipientLanguage,
            };

            sessionStorage.setItem(this.defaultSelectionSessionKey, JSON.stringify(defaults));
        }

        this.closeDialog();
    }

    /** Encode generateContent string into utf-8 byte stream,
     * throw error if greater then 3000 bytes
    */
    checkErrors() {
        const encoder = new TextEncoder();
        const byteArray = encoder.encode(this.generatedContent);
        this.isTextExceedingLimit = byteArray.length > 3000;

        if (this.isTextExceedingLimit) {
            requestAnimationFrame(() => {
                const content = document.querySelector('#generated_content');
                const listener = new ResizeObserver((entries) => {
                    if (this.rightScrollPanel?.contentDoesVerticalScroll()) {
                        this.rightScrollPanel.scrollTo(entries[0].contentRect.bottom);
                    }
                });
                if (listener && content) {
                    listener.observe(content);
                }
            });
        }
    }


    toggleDisclaimer(state: boolean) {
        this.disclaimerState = state;
    }

    setDisclaimerState() {
        const disclaimer: DisclaimerSession = {
            hideDisclaimer: this.hideDisclaimer
        };
        sessionStorage.setItem(this.disclaimerSessionKey, JSON.stringify(disclaimer));
    }


    desiredOutcomeChanged(desiredOutcome: DropdownOptionModel<DesiredOutputType>) {
        switch(desiredOutcome.value) {
            case DesiredOutputType.EARN_MEDIA:
                this.desiredOutcomeSubOptions = this.earnMediaSubOptions;
                this.desiredOutcomeSub = this.earnMediaSubOptions[0];
                break;
            case DesiredOutputType.ENGAGE:
                this.desiredOutcomeSubOptions = this.engageSubOptions;
                this.desiredOutcomeSub = this.engageSubOptions[0];
                break;
            case DesiredOutputType.COLLABORATE:
                this.desiredOutcomeSubOptions = this.collaborateSubOptions;
                this.desiredOutcomeSub = this.collaborateSubOptions[0];
                break;
            case DesiredOutputType.BUILD_RELATIONSHIP:
                this.desiredOutcomeSubOptions = this.buildRelationshipSubOptions;
                this.desiredOutcomeSub = this.buildRelationshipSubOptions[0];
                break;
            case DesiredOutputType.NONE:
                break;
        }
    }

    onComplete() {
        this.streamingStarted = false;
        this.loading = false;
    }

    handleError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: 'Common.dialog.aiHelper.error.title',
                detail: 'Common.dialog.aiHelper.error.description'
            }
        );
        this.loading = false;
    }

    private loadSessionDefaults() {
        const savedDefaults = sessionStorage.getItem(this.defaultSelectionSessionKey);
        if (savedDefaults) {
            try {
                const data = JSON.parse(savedDefaults) as PersonalizationWorkbenchDefault;

                this.desiredOutcome = this.desiredOutcomeOptions.find(x => x.value === data.desiredOutcome);

                if (data.desiredOutcome !== null) {
                    const allOptions = [
                        ...this.earnMediaSubOptions,
                        ...this.engageSubOptions,
                        ...this.collaborateSubOptions,
                        ...this.buildRelationshipSubOptions
                    ];

                    this.desiredOutcomeSub = allOptions.find(x => x.value === data.desiredOutcomeSub);
                    switch(this.desiredOutcome.value) {
                        case DesiredOutputType.EARN_MEDIA:
                            this.desiredOutcomeSubOptions = this.earnMediaSubOptions;
                            break;
                        case DesiredOutputType.ENGAGE:
                            this.desiredOutcomeSubOptions = this.engageSubOptions;
                            break;
                        case DesiredOutputType.COLLABORATE:
                            this.desiredOutcomeSubOptions = this.collaborateSubOptions;
                            break;
                        case DesiredOutputType.BUILD_RELATIONSHIP:
                            this.desiredOutcomeSubOptions = this.buildRelationshipSubOptions;
                            break;
                        case DesiredOutputType.NONE:
                            break;
                    }
                }

                if (data.customDesiredOutcome) {
                    this.customDesiredOutcome = data.customDesiredOutcome;
                }

                this.tone = this.toneOptions.find(x => x.value === data.tone);
                this.length = this.lengthOptions.find(x => x.value === data.length);
                this.urgency = this.urgencyOptions.find(x => x.value === data.urgency);
                this.language = this.languageOptions.find(x => x.value === data.language);
                this.useRecipientLanguage = data.useRecipientLanguage;
            } catch (err) {
                // fail silently ¯\_(ツ)_/¯
            }
        }

        const disclaimerState = sessionStorage.getItem(this.disclaimerSessionKey);

        if (disclaimerState) {
            try {
                const data = JSON.parse(disclaimerState) as DisclaimerSession;
                this.storedHideDisclaimer = data.hideDisclaimer;
            } catch (err) {
                // fail silently ¯\_(ツ)_/¯
            }
        }
    }
}
